/**
 * The home page: what the two apps are, and why they belong together.
 *
 * Copy is in the user's terms rather than the system's. "Entre no seu projeto e
 * pergunte" rather than "agent runtime with MCP tools"; "um arquivo só" rather
 * than "shared session file". The technical vocabulary is where it belongs, in the
 * download and requirements lines, where somebody choosing an installer is
 * already looking at it.
 *
 * Every sentence here comes from the dictionary for the language the address bar is
 * in, and every sentence about an app comes from `appsFor(locale)`. Nothing in this
 * file is written in one language and expected to work in the other two.
 *
 * Layout, in one line each, because four sections that share a layout read as one
 * section repeated:
 *
 *   hero          the crest draws itself in, then the sentence that says what it is
 *   the two apps   the product plate beside its name, the feature grid underneath
 *   one account    three columns on one horizontal thread
 *   pricing teaser one band, split left to right
 */

import React, { useEffect, useState } from 'react';
import { ArrowRight, Download } from 'lucide-react';

import { hasApp, useAuth } from '../auth/AuthProvider';
import { appsFor } from '../content/apps';
import { format, RichText, useI18n } from '../i18n';
import { Link } from '../router';

import { HipercubeLottie } from './HipercubeLottie';
import { PricingTeaser } from './PricingView';

const WorkLogoCycle: React.FC = () => {
  const sources = [
    '/OrobasWorkLogoNew.svg',
    '/OrobasWorkLogoNewSilver.svg',
    '/OrobasWorkLogoNewGold.svg',
  ];
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % sources.length);
    }, 2400);
    return () => window.clearInterval(timer);
  }, [sources.length]);
  return (
    <div className="absolute inset-0 flex items-center justify-center">
      {/* Animated gradient glow background */}
      <div
        className="absolute inset-0 bg-[conic-gradient(from_0deg_at_50%_50%,rgba(237,199,68,0)_0%,rgba(237,199,68,0.15)_25%,rgba(237,199,68,0)_50%,rgba(237,199,68,0.15)_75%,rgba(237,199,68,0)_100%)] animate-spin opacity-30 pointer-events-none"
        style={{ animationDuration: '20s', animationTimingFunction: 'linear' }}
        aria-hidden="true"
      />
      <img
        key={sources[index]}
        src={sources[index]}
        alt="Orobas Work"
        className="size-[70%] object-contain p-6 hipercube-slide-in opacity-80"
        loading="lazy"
        decoding="async"
      />
    </div>
  );
};

export const Home: React.FC = () => {
  // `entitlements` is read inside TheTwoApps, which is a separate component with
  // its own hook call. Destructuring it here as well would be a second read of
  // the same value for nothing.
  const { status } = useAuth();

  return (
    <>
      <Hero status={status} />
      <TheTwoApps />
      <HowTheyWorkTogether />
      <PricingTeaser />
    </>
  );
};

/**
 * The opening moment.
 *
 * The crest is the subject and the headline is the caption, which is why the mark is
 * above the words rather than beside them. Everything in here is centred, because on
 * a product page the message itself is the composition, the same reason Apple's own
 * hero is centred rather than split.
 *
 * The `[@media(max-height:700px)]` variants below are the hero holding its shape on a
 * short window. Only the spacing tightens: on a 360x640 phone the buttons landed
 * 14px below the fold, and a hero whose call to action needs a scroll is a hero that
 * did not finish.
 */
const Hero: React.FC<{ status: string }> = ({ status }) => {
  const { locale, t } = useI18n();

  /*
    Resolved per render rather than once at module load, because the shortcut lives
    on an app and the app copy is language-specific. Left as `| undefined` rather
    than defaulted with `?? apps[0]`: if a future entry has no shortcut there is no
    honest line to print, and a fallback would put a chord on the page that opens
    nothing.
  */
  const agent = appsFor(locale).find((app) => app.shortcut !== null);

  return (
    <section className="relative overflow-hidden px-5 pb-8 pt-10 [@media(max-height:700px)]:pb-6 [@media(max-height:700px)]:pt-4 sm:px-8 sm:pb-10 sm:pt-12">
      {/*
        No glow is painted behind the crest. The crest is an opaque video whose own
        pixels were composited onto this page's background colour and encoded with an
        explicit full range, so its rectangle is the page's colour to within one 8-bit
        step. A gradient here would light the page and leave the rectangle as a dark
        patch sitting in the light, which is exactly what it looked like before.

        The wash below it is therefore pushed clear of the crest's box, and does the
        job the crest cannot: it keeps the headline off flat black.
      */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(64% 26% at 50% 78%, rgba(255,255,255,0.05), transparent 76%)',
        }}
      />

      <div className="relative mx-auto flex max-w-4xl flex-col items-center text-center">
        {/*
          Sized from height as well as width. A crest that fits a 1440x900 laptop is
          80px too tall for a 1280x720 one, and that is the difference between the
          buttons being visible and not. `max(130px, 21vh)` keeps it sensible on a
          tall phone without letting it collapse on a short one.
        */}
        <div className="relative mx-auto flex max-w-4xl flex-col items-center text-center">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 w-full">
            {/* Logo SVG + "Work" text - responsive layout */}
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 w-full sm:w-auto justify-center">
              <div className="aspect-square w-[min(100px,max(70px,12vh))] sm:w-[min(140px,max(90px,18vh))] flex-shrink-0">
                <img src="/OrobasWorkLogoNewGold.svg" alt="Orobas Work" width={120} height={120} className="block h-full w-full object-contain" />
              </div>
              <span
                className="font-bold leading-none bg-gradient-to-r from-gold via-amber-300 to-yellow-400 bg-clip-text text-transparent animate-letter-spacing text-center sm:text-left"
                style={{
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", sans-serif',
                  fontSize: 'min(100px, max(70px, 12vh))',
                  lineHeight: '1',
                }}
              >
                Work
              </span>
            </div>
            <span
              aria-hidden="true"
              className="text-[clamp(1.5rem,4vh,3rem)] font-light leading-none text-muted flex-shrink-0"
            >
              +
            </span>
            {/* Hipercube com brilho radial dourado MAIS FORTE - efeito metálico */}
            <div className="relative aspect-square w-[min(140px,max(90px,14vh))] sm:w-[min(200px,max(120px,22vh))] flex-shrink-0">
              <HipercubeLottie
                plan="gold"
                size="full"
                title={null}
                className="h-full w-full relative z-10 hipercube-gold-glow"
              />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(237,199,68,0.55)_0%,_rgba(237,199,68,0.25)_40%,_rgba(237,199,68,0)_75%)] pointer-events-none" />
            </div>
          </div>
        </div>

        {/* The shortcut, directly under the mark it opens. It is the product's one
            constant, so it belongs with the mark and not as a footnote under the
            buttons. */}
        {agent !== undefined && (
          <p className="mt-1 inline-flex items-center gap-2 text-[14px] text-muted">
            <kbd className="rounded-[7px] border border-line-strong bg-white/6 px-2 py-0.5 text-[13px] font-medium text-cream">
              {agent.shortcut}
            </kbd>
            {format(t.home.heroShortcut, { app: agent.name })}
          </p>
        )}

        {/*
          `min(6.6vw, 9vh)` is what keeps the headline from becoming the reason the
          buttons are below the fold: on a wide-but-short window the width term would
          ask for 80px and the height term caps it lower.
        */}
        <div className="mt-6 [@media(max-height:700px)]:mt-4 sm:mt-7">
          <a
            href="/downloads/OrobasInstaller.zip"
            download
            className="pill pressable inline-flex items-center gap-2 bg-gold px-7 py-3.5 text-[15px] text-ink hover:bg-gold-bright"
          >
            <Download className="size-4" aria-hidden="true" />
            {t.home.installerDownload}
          </a>
        </div>

        <p className="mt-5 max-w-3xl text-[16px] leading-[1.55] text-muted [@media(max-height:700px)]:mt-4 sm:mt-6 sm:text-[19px]">
          <RichText text={t.home.heroBody} values={{ agent: 'OrobasHipercube', work: 'Orobas Work' }} />
        </p>

        <div className="mt-8 flex flex-col items-center justify-center gap-3 [@media(max-height:700px)]:mt-6 sm:mt-9 sm:flex-row sm:flex-wrap">
          {/*
            One link, one label. The two branches were identical except for the word
            on the button, and having them as separate elements meant a future change
            to one would leave the other behind.
          */}
          <Link
            to="conta"
            className="pill pressable bg-gold px-7 py-3.5 text-[15px] text-ink hover:bg-gold-bright"
          >
            {status === 'signed-in' ? t.home.ctaAccount : t.home.ctaSignIn}
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>

          <a
            href="#aplicativos"
            className="pill pressable border border-line-strong px-7 py-3.5 text-[15px] text-cream hover:bg-white/8"
          >
            {t.home.ctaMeetApps}
          </a>
        </div>
      </div>
    </section>
  );
};

/**
 * The two apps.
 *
 * Alternating, one column each, with the app's own features. The earlier version
 * was a grid of identical feature cards, which said nothing about which app does what:
 * both columns looked the same, so the reader had to read to find out that one is an
 * agent and the other is an editor.
 *
 * Each app gets a plate that is the app's own surface, not a picture of one. The
 * Agent's whole compact mode is a 72x72 crest in the menu bar, so its plate is the
 * crest. The Work's whole premise is a sheet of glass over your desktop, so its plate
 * is the wallpaper with the glass still on it.
 */
const TheTwoApps: React.FC = () => {
  const { locale } = useI18n();
  const { entitlements, status } = useAuth();

  return (
    <section id="aplicativos" className="px-5 py-20 sm:px-8 sm:py-28">
      <div className="mx-auto max-w-6xl space-y-24 sm:space-y-32">
        {appsFor(locale).map((app, index) => (
          <OneApp
            key={app.id}
            app={app}
            flipped={index % 2 === 1}
            owned={hasApp(entitlements, app.id)}
            signedIn={status === 'signed-in'}
          />
        ))}
      </div>
    </section>
  );
};

const OneApp: React.FC<{
  app: ReturnType<typeof appsFor>[number];
  flipped: boolean;
  owned: boolean;
  signedIn: boolean;
}> = ({ app, flipped, owned, signedIn }) => {
  const { t } = useI18n();

  return (
    <article>
      <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-14">
        <div className={`lg:col-span-6 ${flipped ? 'lg:order-2' : ''}`}>
          <AppPlate app={app} />
        </div>

        <div className={`lg:col-span-6 ${flipped ? 'lg:order-1' : ''}`}>
          <p className="text-[13px] font-medium text-gold">{app.role}</p>
          <h2 className="mt-2.5 text-[clamp(1.9rem,3.6vw,2.9rem)] font-semibold leading-[1.06] tracking-[-0.028em] text-cream">
            {app.name}
          </h2>
          <p className="mt-3 text-[19px] leading-snug text-gold/90">{app.tagline}</p>
          <p className="mt-5 max-w-xl text-[17px] leading-[1.55] text-muted">{app.summary}</p>

          {app.shortcut !== null && (
            <p className="mt-5 inline-flex items-center gap-2 text-[13px] text-muted">
              <kbd className="rounded-[6px] border border-line-strong bg-white/6 px-2 py-0.5 font-mono text-[12px] text-cream">
                {app.shortcut}
              </kbd>
              {format(t.home.opensApp, { app: app.name })}
            </p>
          )}

          {signedIn && (
            <p
              className={`mt-6 inline-flex items-center gap-2 rounded-full border px-3.5 py-1 text-[12px] ${
                owned ? 'border-gold/40 text-gold' : 'border-line-strong text-muted'
              }`}
            >
              {owned ? t.home.ownedBadge : t.home.notOwnedBadge}
            </p>
          )}
        </div>
      </div>

      <ul className="mt-12 grid gap-x-10 gap-y-9 sm:grid-cols-2 lg:mt-14 lg:grid-cols-3">
        {app.features.map((feature) => {
          // The icon belongs to the feature, not to its position in the list. Choosing
          // it by index meant reordering the list silently changed which icon a
          // feature wore, and indexing into a fixed array of six is what produced the
          // `| undefined` this replaced.
          const FeatureIcon = feature.icon;
          return (
            <li key={feature.id}>
              <FeatureIcon className="size-[18px] text-gold/85" aria-hidden="true" />
              <h3 className="mt-3.5 text-[16px] font-semibold tracking-[-0.01em] text-cream">
                {feature.title}
              </h3>
              <p className="mt-2 text-[15px] leading-[1.55] text-muted">{feature.detail}</p>
            </li>
          );
        })}
      </ul>
    </article>
  );
};

/**
 * The plate beside the name.
 *
 * Not a screenshot of the app, because there is none to show that is honest: a
 * hand-built window of styled divs is a drawing of a product, not the product. Both
 * plates are real assets instead, the crest the Agent actually is, and the actual
 * wallpaper the Work app lays its glass over.
 */
const AppPlate: React.FC<{ app: ReturnType<typeof appsFor>[number] }> = ({ app }) => {
  const { t } = useI18n();

  return (
    <div className="glass overflow-hidden rounded-glass">
      {app.id === 'orobas_work' ? (
        <div className="relative aspect-square w-full overflow-hidden rounded-glass bg-panel/40">
           {/*
             The Work's own mark, in the three plan colours. Cycles like the
             Hipercube plate so the two apps read as one family on the page.
           */}
           <WorkLogoCycle />
        </div>
      ) : (
        <div className="relative flex aspect-square w-full items-center justify-center">
          {/* The animated cube the app itself ships, cycling through every plan. */}
          <HipercubeLottie
            plans={['default', 'silver', 'gold']}
            cycleOnComplete
            size="full"
            title={format(t.home.plateAlt, { app: app.name })}
            className="relative h-full w-full hipercube-slide-in hipercube-glow"
          />
        </div>
      )}
    </div>
  );
};

/**
 * The session, in the only terms it is actually true in.
 *
 * "Sincronizado entre o site e os aplicativos" would be a claim this page cannot
 * make: a browser cannot read the file the two apps share. What is true is that one
 * account and one licence are read by all three, and this is what says so.
 */
const HowTheyWorkTogether: React.FC = () => {
  const { locale, t } = useI18n();
  const requirements = [...new Set(appsFor(locale).map((app) => app.requirements))].join(' · ');

  return (
    <section className="border-y border-line px-5 py-24 sm:px-8 sm:py-32">
      <div className="mx-auto max-w-5xl">
        <h2 className="max-w-3xl text-[clamp(1.9rem,4.2vw,3.1rem)] font-semibold leading-[1.06] tracking-[-0.028em] text-cream">
          {t.home.togetherTitle}
        </h2>
        <p className="mt-6 max-w-2xl text-[17px] leading-[1.55] text-muted">{t.home.togetherBody}</p>

        {/* One rule under all three, because the three are one thing at three sizes. */}
        <div className="mt-14 border-t border-line-strong pt-10">
          <dl className="grid gap-10 sm:grid-cols-3 sm:gap-8">
            {t.home.togetherColumns.map((entry) => (
              <div key={entry.title}>
                <dt className="text-[15px] font-semibold text-cream">{entry.title}</dt>
                <dd className="mt-2 text-[15px] leading-[1.5] text-muted">{entry.body}</dd>
              </div>
            ))}
          </dl>
        </div>

        {/*
          Deduplicated: both apps declare the same requirement, and joining the list
          as-is printed "macOS 26 ou posterior · macOS 26 ou posterior".
        */}
        <p className="mt-12 text-[13px] text-muted">
          {format(t.home.requirementsLine, { requirements })}
        </p>

        {/* Ver planos button at the bottom */}
        <div className="mt-14 text-center">
          <Link
            to="planos"
            className="pill pressable inline-flex items-center gap-2 bg-gold px-6 py-3.5 text-[15px] text-ink hover:bg-gold-bright"
          >
            <img src="/OrobasWorkLogoNewGold.svg" alt={t.pricing.teaserCta} width={17} height={17} className="block" />
            {t.pricing.teaserCta}
          </Link>
        </div>
      </div>
    </section>
  );
};